import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CbtStudentImports } from '../serverurl';
import { ajaxconfig } from '../ajaxconfig';

const ExcelUpload = () => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const config = ajaxconfig();
  const [serverData, setServerData] = useState([]);
  const [allowedTestIds, setAllowedTestIds] = useState([]);

  const { cbt_pss_code } = useParams();  
  const requiredHeaders = ['cbt_pass_code', 'test_id', 'test_information'];

  // Fetch server data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = `${CbtStudentImports}/${cbt_pss_code}`;
        const response = await axios.get(url, config);
        
        // Handle both array and object responses
        const responseData = Array.isArray(response.data) 
          ? response.data 
          : response.data.data || [];
          
        setServerData(responseData);
        
        // Extract unique test IDs
        const uniqueTestIds = [...new Set(responseData.map(item => item.test_id))];
        setAllowedTestIds(uniqueTestIds);
      } catch (error) {
        console.error('Fetch error:', error);
        toast.error('Failed to load server data');
      }
    };

    fetchData();
  }, [cbt_pss_code]);

  // Drag and drop handlers
  const handleDragEvents = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files[0]) validateAndSetFile(e.dataTransfer.files[0]);
  }, []);

  // File validation and processing
  const validateAndSetFile = (file) => {
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      toast.error('Invalid file type. Please upload Excel or CSV.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File is too large. Max 5MB.');
      return;
    }
    setFile(file);
    readExcelPreview(file);
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) validateAndSetFile(e.target.files[0]);
  };

  // Excel preview processing
  const readExcelPreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });

        if (jsonData.length < 2) {
          toast.error('File is empty.');
          return;
        }

        const headers = jsonData[0].map(h => String(h).trim().toLowerCase());
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h.toLowerCase()));

        if (missingHeaders.length) {
          toast.error(`Missing columns: ${missingHeaders.join(', ')}`);
          return;
        }

        // Get column indexes for required headers
        const headerIndexes = requiredHeaders.map(header => 
          headers.findIndex(h => h === header.toLowerCase())
        );

        // Process data rows
        const processedData = jsonData.slice(1).map(row => 
          headerIndexes.map(idx => (row[idx] !== undefined ? String(row[idx]) : ''))
        );

        setPreviewData(processedData.slice(0)); // Only show first 5 rows
      } catch (err) {
        toast.error('Error reading file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // File submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please select a file.');

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('excel_file', file);

      const response = await axios.post(CbtStudentImports, formData, config);
      toast.success(response.data.message || 'File uploaded successfully!');
      
      // Refresh server data after successful upload
      const refreshResponse = await axios.get(`${CbtStudentImports}/${cbt_pss_code}`, config);
      setServerData(Array.isArray(refreshResponse.data) ? refreshResponse.data : []);
      
      setFile(null);
      setPreviewData([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                           err.response?.data?.errors?.join(', ') || 
                           'Upload failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Upload Student Data</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Upload Excel/CSV files with student information for CBT tests. 
          Ensure your file includes all required columns.
        </p>
      </div>

      {/* Drag & Drop Area */}
      <div 
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all mb-8
          ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'}
          ${file ? 'bg-green-50 border-green-300' : ''}`}
        onDragOver={handleDragEvents}
        onDragLeave={handleDragEvents}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="p-3 bg-indigo-100 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          
          <div className="space-y-1">
            <p className="font-medium text-gray-900">
              {file ? file.name : 'Click or drag file to upload'}
            </p>
            <p className="text-sm text-gray-500">
              {file ? 'File ready for upload' : 'Supports .xlsx, .xls, .csv (Max 5MB)'}
            </p>
          </div>
          
          <button 
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={(e) => e.stopPropagation()}
          >
            Browse Files
          </button>
        </div>
        
        <input
          type="file"
          ref={fileInputRef}
          accept=".xlsx, .xls, .csv"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Preview Table */}
      {previewData.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z" clipRule="evenodd" />
              </svg>
              Preview ({previewData.length} rows shown)
            </h3>
          </div>
          
          <div className="overflow-auto max-h-[500px]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {requiredHeaders.map((header, i) => (
                    <th
                      key={i}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previewData.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {row.map((cell, colIndex) => (
                      <td 
                        key={`${rowIndex}-${colIndex}`}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 max-w-xs truncate"
                        title={cell}
                      >
                        {cell || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end mb-8">
        <button
          onClick={handleSubmit}
          disabled={!file || isLoading}
          className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
            isLoading || !file 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            'Confirm Upload'
          )}
        </button>
      </div>

      {/* Server Data Sections */}
      <div className="space-y-6">
        {/* Allowed Test IDs */}
        {allowedTestIds.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-3 text-indigo-700">Allowed Test IDs</h3>
            <div className="flex flex-wrap gap-2">
              {allowedTestIds.map((id, idx) => (
                <span key={id || idx} className="px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full">
                  {id}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Server Data Table */}
        {serverData.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Students with Access</h3>
              <p className="text-sm text-gray-500 mt-1">{serverData.length} records found</p>
            </div>
            <div className="overflow-auto max-h-[500px]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CBT Pass Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test Information</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serverData.map((row) => (
                    <tr key={`${row.cbt_pass_code}-${row.test_id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-700">{row.cbt_pass_code}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.test_id}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.test_information}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>


      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
<p className="text-sm mt-4 text-center text-gray-400">
  Powered by{" "}
  <a
    href="https://cbthost.com"
    className="text-gray-700 font-medium hover:text-blue-400 transition-colors duration-300"
  >
    cbthost.com
  </a>
</p>

    </div>
  );
};

export default ExcelUpload;